import React, { useState } from 'react';
import { X } from 'lucide-react';
import { signInWithGoogle, signInWithEmailPassword, registerWithEmailPassword } from '../utils/firebase';
import { Text } from './typography/Text';
import { trackAuthentication } from '../utils/analytics';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSigningIn) return;
    
    setIsSigningIn(true);
    setError('');
    
    try {
      if (isRegistering) {
        await registerWithEmailPassword(email, password);
        trackAuthentication('email', true);
      } else {
        await signInWithEmailPassword(email, password);
        trackAuthentication('email', true);
      }
      onClose();
    } catch (error: any) {
      trackAuthentication('email', false, error.message);
      // Handle specific error cases
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
        setError(`No account found with this email. Would you like to create one?`);
        setIsRegistering(true);
      } else if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
        setIsRegistering(false);
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters long.');
      } else {
        setError(error.message || 'Authentication failed');
      }
      console.error('Auth error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isSigningIn) return;
    
    setIsSigningIn(true);
    setError('');
    
    try {
      await signInWithGoogle();
      trackAuthentication('google', true);
      onClose();
    } catch (error: any) {
      trackAuthentication('google', false, error.message);
      setError(error.message || 'Failed to sign in');
      console.error('Sign in error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <Text className="text-2xl font-bold">
            {isRegistering ? 'Create Account' : 'Sign In'}
          </Text>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
              minLength={6}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSigningIn}
            className="w-full bg-primary text-white rounded-lg py-2 hover:bg-primary-hover transition-colors disabled:opacity-50"
          >
            {isSigningIn ? 'Processing...' : (isRegistering ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsRegistering(!isRegistering);
              setError(''); // Clear any existing errors when switching modes
            }}
            className="text-primary hover:text-primary-hover text-sm"
          >
            {isRegistering ? 'Already have an account? Sign in' : 'Need an account? Register'}
          </button>
        </div>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with
              </span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn}
            className="mt-4 w-full flex items-center justify-center space-x-2 border rounded-lg py-2 hover:bg-gray-50 transition-colors"
          >
            <img 
              src="https://cdn4.iconfinder.com/data/icons/social-media-2210/24/Google-512.png" 
              className="w-5 h-5"
              alt="Google"
            />
            <span>Google</span>
          </button>
        </div>
      </div>
    </div>
  );
}